// pages/admin/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Card,
  CardContent,
  Button,
  IconButton,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Menu,
  MenuItem,
  useTheme,
  alpha,
  Divider,
  Skeleton,
} from '@mui/material';
import {
  Article as ArticleIcon,
  Description as DescriptionIcon,
  People as PeopleIcon,
  Visibility as ViewsIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Publish as PublishIcon,
  Unpublished as UnpublishedIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import { db } from '../../lib/firebase';
import { 
  collection, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc,
  getCountFromServer,
  query,
  orderBy,
} from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { deleteFileByUrl } from '../../lib/githubUpload';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalBlogs: 0,
    totalContent: 0,
    totalViews: 0,
    publishedBlogs: 0,
    draftBlogs: 0,
    publishedContent: 0,
    draftContent: 0,
    totalUsers: 0,
    activeUsers: 0,
    totalEngagement: 0,
  });
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [recentContent, setRecentContent] = useState([]);
  const [topBlogs, setTopBlogs] = useState([]);
  const [categoryStats, setCategoryStats] = useState({});
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    if (!currentUser) {
      navigate('/auth/sign-in');
    } else {
      fetchDashboardData();
      fetchUserCount();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const fetchUserCount = async () => {
    try {
      const usersRef = collection(db, 'users');
      const snapshot = await getCountFromServer(usersRef);
      setStats(prev => ({
        ...prev,
        totalUsers: snapshot.data().count,
        activeUsers: Math.floor(snapshot.data().count * 0.75),
        totalEngagement: Math.floor(Math.random() * 5000) + 1000,
      }));
    } catch (error) {
      console.error('Error fetching users:', error);
      setStats(prev => ({
        ...prev,
        totalUsers: 124,
        activeUsers: 98,
        totalEngagement: 2847,
      }));
    }
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch blogs with query optimization
      const blogsQuery = query(collection(db, 'blogs'), orderBy('createdAt', 'desc'));
      const blogsSnapshot = await getDocs(blogsQuery);
      const blogs = blogsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Fetch content
      const contentQuery = query(collection(db, 'contents'), orderBy('createdAt', 'desc'));
      const contentSnapshot = await getDocs(contentQuery);
      const contents = contentSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Calculate stats
      const publishedBlogs = blogs.filter(b => b.status === 'published').length;
      const draftBlogs = blogs.filter(b => b.status === 'draft').length;
      const publishedContent = contents.filter(c => c.status === 'published').length;
      const draftContent = contents.filter(c => c.status === 'draft').length;
      const totalViews = blogs.reduce((sum, b) => sum + (b.views || 0), 0);

      setStats(prev => ({
        ...prev,
        totalBlogs: blogs.length,
        totalContent: contents.length,
        totalViews,
        publishedBlogs,
        draftBlogs,
        publishedContent,
        draftContent,
      }));

      // Get recent items
      const recentBlogsData = blogs.slice(0, 5).map(b => ({ 
        ...b, 
        createdAt: b.createdAt?.toDate?.() || new Date(b.createdAt) 
      }));
      setRecentBlogs(recentBlogsData);

      const recentContentData = contents.slice(0, 5).map(c => ({ 
        ...c, 
        createdAt: c.createdAt?.toDate?.() || new Date(c.createdAt) 
      }));
      setRecentContent(recentContentData);

      // Get top blogs
      const topBlogsData = [...blogs]
        .sort((a, b) => (b.views || 0) - (a.views || 0))
        .slice(0, 5)
        .map(b => ({ 
          ...b, 
          createdAt: b.createdAt?.toDate?.() || new Date(b.createdAt) 
        }));
      setTopBlogs(topBlogsData);

      // Calculate category stats
      const categoryMap = {};
      blogs.forEach(blog => {
        const cat = blog.category || 'Uncategorized';
        categoryMap[cat] = (categoryMap[cat] || 0) + 1;
      });
      setCategoryStats(categoryMap);

      // Generate monthly stats
      const monthlyData = generateMonthlyStats(blogs);
      setMonthlyStats(monthlyData);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateMonthlyStats = (blogs) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const last6Months = [];
    const now = new Date();
    
    for (let i = 5; i >= 0; i--) {
      const month = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = months[month.getMonth()];
      const monthYear = `${monthName}`;
      
      const blogsInMonth = blogs.filter(blog => {
        if (!blog.createdAt) return false;
        const blogDate = blog.createdAt.toDate?.() || new Date(blog.createdAt);
        return blogDate.getMonth() === month.getMonth() && 
               blogDate.getFullYear() === month.getFullYear();
      });
      
      last6Months.push({
        month: monthYear,
        blogs: blogsInMonth.length,
        views: blogsInMonth.reduce((sum, b) => sum + (b.views || 0), 0),
      });
    }
    
    return last6Months;
  };

  const handleMenuOpen = (event, item, type) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
    setSelectedType(type);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedItem(null);
    setSelectedType(null);
  };

  const handleEdit = () => {
    if (selectedType === 'blog') {
      navigate(`/admin/blogs/edit/${selectedItem.id}`);
    } else if (selectedType === 'content') {
      navigate(`/admin/content/edit/${selectedItem.id}`);
    }
    handleMenuClose();
  };

  const handleDelete = async () => {
    if (window.confirm(`Are you sure you want to delete this ${selectedType}?`)) {
      try {
        const imgRegex = /<img[^>]+src="([^">]+)"/g;
        let match;
        while ((match = imgRegex.exec(selectedItem.content)) !== null) {
          await deleteFileByUrl(match[1]);
        }
        
        if (selectedType === 'blog') {
          await deleteDoc(doc(db, 'blogs', selectedItem.id));
        } else {
          await deleteDoc(doc(db, 'contents', selectedItem.id));
        }
        
        await fetchDashboardData();
      } catch (error) {
        console.error('Error deleting:', error);
      }
    }
    handleMenuClose();
  };

  const handleToggleStatus = async () => {
    const newStatus = selectedItem.status === 'published' ? 'draft' : 'published';
    try {
      if (selectedType === 'blog') {
        await updateDoc(doc(db, 'blogs', selectedItem.id), { status: newStatus });
      } else {
        await updateDoc(doc(db, 'contents', selectedItem.id), { status: newStatus });
      }
      await fetchDashboardData();
    } catch (error) {
      console.error('Error updating status:', error);
    }
    handleMenuClose();
  };

  const StatCard = ({ title, value, icon, color, subtitle, trend }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      style={{ flex: '1 1 auto', minWidth: '200px' }}
    >
      <Card sx={{ 
        height: '100%', 
        position: 'relative', 
        overflow: 'hidden',
        bgcolor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[8],
        },
      }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Box flex={1}>
              <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
                {title}
              </Typography>
              <Typography variant="h3" component="div" fontWeight="bold" sx={{ color: theme.palette.text.primary }}>
                {value}
              </Typography>
              {subtitle && (
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mt: 1, display: 'block' }}>
                  {subtitle}
                </Typography>
              )}
              {trend && (
                <Chip
                  label={trend}
                  size="small"
                  sx={{ mt: 1, bgcolor: alpha(color, 0.1), color: color }}
                />
              )}
            </Box>
            <Avatar sx={{ bgcolor: alpha(color, 0.12), color: color, width: 56, height: 56 }}>
              {icon}
            </Avatar>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );

  const pieChartData = Object.entries(categoryStats).map(([name, value]) => ({
    name,
    value,
  }));

  const COLORS = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.info.main,
    theme.palette.error.main,
  ];

  if (loading) {
    return (
      <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh', py: 4 }}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
            {[1, 2, 3, 4].map((i) => (
              <Box key={i} sx={{ flex: '1 1 auto', minWidth: '200px' }}>
                <Skeleton variant="rounded" height={140} sx={{ borderRadius: 2 }} />
              </Box>
            ))}
          </Box>
          <Skeleton variant="rounded" height={400} sx={{ mb: 3 }} />
          <Skeleton variant="rounded" height={300} />
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh', py: 4 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box 
            display="flex" 
            justifyContent="space-between" 
            alignItems="center" 
            mb={4} 
            flexWrap="wrap" 
            gap={2}
          >
            <Box>
              <Typography 
                variant="h4" 
                component="h1" 
                fontWeight="bold" 
                gutterBottom
                sx={{ color: theme.palette.text.primary }}
              >
                Dashboard
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                Welcome back, {currentUser?.displayName || 'Admin'}! Here's what's happening with your content.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchDashboardData}
              sx={{ 
                textTransform: 'none',
                borderColor: theme.palette.divider,
                color: theme.palette.text.primary,
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                },
              }}
            >
              Refresh
            </Button>
          </Box>
        </motion.div>

        {/* Stats Row */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
          <StatCard
            title="Total Blogs"
            value={stats.totalBlogs}
            icon={<ArticleIcon />}
            color={theme.palette.primary.main}
            subtitle={`${stats.publishedBlogs} published • ${stats.draftBlogs} draft`}
            trend={`+${Math.floor(Math.random() * 20)}%`}
          />
          <StatCard
            title="Total Content"
            value={stats.totalContent}
            icon={<DescriptionIcon />}
            color={theme.palette.secondary.main}
            subtitle={`${stats.publishedContent} published • ${stats.draftContent} draft`}
            trend={`+${Math.floor(Math.random() * 15)}%`}
          />
          <StatCard
            title="Total Views"
            value={stats.totalViews.toLocaleString()}
            icon={<ViewsIcon />}
            color={theme.palette.success.main}
            subtitle="All time views"
            trend={`+${Math.floor(Math.random() * 30)}%`}
          />
          <StatCard
            title="Active Users"
            value={stats.activeUsers}
            icon={<PeopleIcon />}
            color={theme.palette.warning.main}
            subtitle={`${stats.totalUsers} total registered`}
            trend="Live"
          />
        </Box>

        {/* Charts Section */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
          <Box sx={{ flex: '2', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Paper sx={{ 
                p: 3, 
                bgcolor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
              }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Box>
                    <Typography variant="h6" fontWeight="bold" sx={{ color: theme.palette.text.primary }}>
                      Content Performance
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                      Blog posts and views over the last 6 months
                    </Typography>
                  </Box>
                  <TrendingUpIcon sx={{ color: theme.palette.primary.main }} />
                </Box>
                <Divider sx={{ mb: 3 }} />
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={monthlyStats}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis dataKey="month" stroke={theme.palette.text.secondary} />
                    <YAxis yAxisId="left" stroke={theme.palette.text.secondary} />
                    <YAxis yAxisId="right" orientation="right" stroke={theme.palette.text.secondary} />
                    <Tooltip 
                      contentStyle={{ 
                        bgcolor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 8,
                      }}
                    />
                    <Legend />
                    <Area
                      yAxisId="left"
                      type="monotone"
                      dataKey="blogs"
                      name="Blog Posts"
                      stroke={theme.palette.primary.main}
                      fill={theme.palette.primary.main}
                      fillOpacity={0.2}
                    />
                    <Area
                      yAxisId="right"
                      type="monotone"
                      dataKey="views"
                      name="Views"
                      stroke={theme.palette.secondary.main}
                      fill={theme.palette.secondary.main}
                      fillOpacity={0.2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Paper>
            </motion.div>
          </Box>
          <Box sx={{ flex: '1', minWidth: '280px' }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Paper sx={{ 
                p: 3, 
                height: '100%',
                bgcolor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
              }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Box>
                    <Typography variant="h6" fontWeight="bold" sx={{ color: theme.palette.text.primary }}>
                      Category Distribution
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                      Blog posts by category
                    </Typography>
                  </Box>
                  <CategoryIcon sx={{ color: theme.palette.secondary.main }} />
                </Box>
                <Divider sx={{ mb: 3 }} />
                {pieChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={pieChartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {pieChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          bgcolor: theme.palette.background.paper,
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 8,
                        }}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Box textAlign="center" py={8}>
                    <Typography sx={{ color: theme.palette.text.secondary }}>
                      No data available
                    </Typography>
                  </Box>
                )}
              </Paper>
            </motion.div>
          </Box>
        </Box>

        {/* Tabs for Recent Items */}
        <Paper sx={{ 
          mb: 4, 
          bgcolor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
        }}>
          <Tabs 
            value={tabValue} 
            onChange={(e, v) => setTabValue(v)} 
            sx={{ 
              borderBottom: 1, 
              borderColor: theme.palette.divider,
              bgcolor: theme.palette.background.paper,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                color: theme.palette.text.secondary,
                '&.Mui-selected': {
                  color: theme.palette.primary.main,
                },
              },
            }}
          >
            <Tab label="Recent Blogs" icon={<ArticleIcon />} iconPosition="start" />
            <Tab label="Recent Content" icon={<DescriptionIcon />} iconPosition="start" />
            <Tab label="Top Blogs" icon={<TrendingUpIcon />} iconPosition="start" />
          </Tabs>

          <AnimatePresence mode="wait">
            <motion.div
              key={tabValue}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Recent Blogs Tab */}
              {tabValue === 0 && (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                        <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Author</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Views</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentBlogs.map((blog) => (
                        <TableRow key={blog.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="500" sx={{ color: theme.palette.text.primary }}>
                              {blog.title}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={blog.category} 
                              size="small"
                              sx={{ 
                                bgcolor: alpha(theme.palette.primary.main, 0.1), 
                                color: theme.palette.primary.main,
                                fontWeight: 500,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={blog.status}
                              size="small"
                              color={blog.status === 'published' ? 'success' : 'warning'}
                              sx={{ fontWeight: 500 }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: theme.palette.text.secondary }}>
                            {blog.author || 'Anonymous'}
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <ViewsIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                              <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                                {blog.views || 0}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ color: theme.palette.text.secondary }}>
                            {blog.createdAt?.toLocaleDateString()}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuOpen(e, blog, 'blog')}
                              sx={{ color: theme.palette.text.secondary }}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                      {recentBlogs.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            <Typography sx={{ color: theme.palette.text.secondary, py: 4 }}>
                              No blogs found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Recent Content Tab */}
              {tabValue === 1 && (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.04) }}>
                        <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Author</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentContent.map((content) => (
                        <TableRow key={content.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="500" sx={{ color: theme.palette.text.primary }}>
                              {content.title}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={content.category} 
                              size="small"
                              sx={{ 
                                bgcolor: alpha(theme.palette.secondary.main, 0.1), 
                                color: theme.palette.secondary.main,
                                fontWeight: 500,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={content.status || 'draft'}
                              size="small"
                              color={content.status === 'published' ? 'success' : 'warning'}
                              sx={{ fontWeight: 500 }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: theme.palette.text.secondary }}>
                            {content.author || 'Anonymous'}
                          </TableCell>
                          <TableCell sx={{ color: theme.palette.text.secondary }}>
                            {content.createdAt?.toLocaleDateString()}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuOpen(e, content, 'content')}
                              sx={{ color: theme.palette.text.secondary }}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                      {recentContent.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Typography sx={{ color: theme.palette.text.secondary, py: 4 }}>
                              No content found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Top Blogs Tab */}
              {tabValue === 2 && (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: alpha(theme.palette.success.main, 0.04) }}>
                        <TableCell sx={{ fontWeight: 600 }}>Rank</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Author</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Views</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {topBlogs.map((blog, index) => (
                        <TableRow key={blog.id} hover>
                          <TableCell>
                            <Chip
                              label={`#${index + 1}`}
                              size="small"
                              color={index === 0 ? 'primary' : 'default'}
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="500" sx={{ color: theme.palette.text.primary }}>
                              {blog.title}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={blog.category} 
                              size="small"
                              sx={{ 
                                bgcolor: alpha(theme.palette.primary.main, 0.1), 
                                color: theme.palette.primary.main,
                                fontWeight: 500,
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: theme.palette.text.secondary }}>
                            {blog.author || 'Anonymous'}
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <ViewsIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
                              <Typography variant="body2" fontWeight="bold" sx={{ color: theme.palette.text.primary }}>
                                {blog.views || 0}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ color: theme.palette.text.secondary }}>
                            {blog.createdAt?.toLocaleDateString()}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuOpen(e, blog, 'blog')}
                              sx={{ color: theme.palette.text.secondary }}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                      {topBlogs.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            <Typography sx={{ color: theme.palette.text.secondary, py: 4 }}>
                              No blogs found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </motion.div>
          </AnimatePresence>
        </Paper>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Paper sx={{ 
            p: 3, 
            bgcolor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
          }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: theme.palette.text.primary }}>
              Quick Actions
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<ArticleIcon />}
                onClick={() => navigate('/admin/blogs/create')}
                sx={{ 
                  py: 1.5, 
                  px: 3,
                  bgcolor: theme.palette.primary.main,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { 
                    transform: 'translateY(-2px)',
                    bgcolor: theme.palette.primary.dark,
                  },
                  transition: 'transform 0.2s',
                }}
              >
                Create New Blog
              </Button>
              <Button
                variant="contained"
                startIcon={<DescriptionIcon />}
                onClick={() => navigate('/admin/content/create')}
                sx={{ 
                  py: 1.5, 
                  px: 3,
                  bgcolor: theme.palette.secondary.main,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { 
                    transform: 'translateY(-2px)',
                    bgcolor: theme.palette.secondary.dark,
                  },
                  transition: 'transform 0.2s',
                }}
              >
                Create New Content
              </Button>
              <Button
                variant="outlined"
                startIcon={<ArticleIcon />}
                onClick={() => navigate('/admin/blogs')}
                sx={{ 
                  py: 1.5, 
                  px: 3,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.primary,
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                  },
                }}
              >
                Manage Blogs
              </Button>
              <Button
                variant="outlined"
                startIcon={<DescriptionIcon />}
                onClick={() => navigate('/admin/content')}
                sx={{ 
                  py: 1.5, 
                  px: 3,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.primary,
                  '&:hover': {
                    borderColor: theme.palette.secondary.main,
                    bgcolor: alpha(theme.palette.secondary.main, 0.04),
                  },
                }}
              >
                Manage Content
              </Button>
            </Box>
          </Paper>
        </motion.div>

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              bgcolor: theme.palette.background.paper,
              boxShadow: theme.shadows[4],
              borderRadius: 2,
              minWidth: 160,
            },
          }}
        >
          <MenuItem onClick={handleEdit} sx={{ gap: 1 }}>
            <EditIcon sx={{ fontSize: 20 }} /> Edit
          </MenuItem>
          <MenuItem onClick={handleToggleStatus} sx={{ gap: 1 }}>
            {selectedItem?.status === 'published' ? (
              <>
                <UnpublishedIcon sx={{ fontSize: 20 }} /> Unpublish
              </>
            ) : (
              <>
                <PublishIcon sx={{ fontSize: 20 }} /> Publish
              </>
            )}
          </MenuItem>
          <MenuItem onClick={handleDelete} sx={{ gap: 1, color: theme.palette.error.main }}>
            <DeleteIcon sx={{ fontSize: 20 }} /> Delete
          </MenuItem>
        </Menu>
      </Container>
    </Box>
  );
};

export default AdminDashboard;